import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  Tabs,
  Card,
  message,
  ConfigProvider,
  Select,
  Alert,
  Typography,
  Row,
  Col,
  Space,
  Result,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  TeamOutlined,
  LoginOutlined,
  UserAddOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { registerUser, loginUser } from "../apis";
import logo from "../Assets/logonavbarexis.svg";
import traffixLogo from "../Assets/TraffixLogo.png";
import "./MysteryClient.css";

const { Title, Text } = Typography;

const MysteryClient = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [form] = Form.useForm();
  const [loginForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();

  const clientOptions = [
    { value: "ASPO", label: "ASPO" },
    { value: "SETA", label: "SETA" },
    { value: "CTM", label: "CTM" },
    { value: "ATM", label: "ATM" },
    { value: "ATB", label: "ATB" },
    { value: "STPS", label: "STPS" },
    { value: "SGM", label: "SGM" },
    { value: "TPER", label: "TPER" },
    { value: "START", label: "START" },
  ];

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const showAlert = (type, messageText) => {
    setAlert({ type, message: messageText });
    setTimeout(() => setAlert(null), 9000);
  };

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await loginUser({
        email: values.email,
        password: values.password,
      });

      localStorage.setItem("authToken", response.token);
      showAlert("success", "Accesso effettuato con successo!");
      message.success("Accesso effettuato con successo!");
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Login error:", error);
      const msg = error?.response?.data?.error || "Errore durante l'accesso";
      showAlert("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const response = await registerUser({
        first_name: values.firstName,
        last_name: values.lastName,
        company: values.client,
        email: values.email,
        password: values.password,
        address: values.address,
        number: values.phone,
        gender: values.gender,
      });

      // Show success state instead of alert
      setRegistrationSuccess(true);
      form.resetFields();

      // Optional: Auto switch to login tab after some time
      setTimeout(() => {
        setRegistrationSuccess(false);
        window.location.reload();
        setActiveTab("login");
      }, 8000);
    } catch (error) {
      const msg =
        error?.response?.data?.error || "Errore durante la registrazione";
      showAlert("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    if (activeTab === "login") {
      handleLogin(values);
    } else {
      handleRegister(values);
    }
  };

  const onFinishFailed = (e) => {
    showAlert("error", "Compila tutti i campi obbligatori correttamente.");
  };

  const onLoginFinishFailed = (e) => {
    showAlert("error", "Compila tutti i campi obbligatori correttamente.");
  };

  const handleBackToRegistration = () => {
    setRegistrationSuccess(false);
    form.resetFields();
  };

  const handleGoToLogin = () => {
    setRegistrationSuccess(false);
    setActiveTab("login");
    window.location.reload();
    form.resetFields();
  };

  // Registration Success Component
  const RegistrationSuccessView = () => (
    <div className="registration-success-container">
      <Result
        icon={<ClockCircleOutlined className="success-icon" />}
        title={
          <div className="success-title">
            <CheckCircleOutlined className="check-icon" />
            <span>Registrazione Completata!</span>
          </div>
        }
        subTitle={
          <div className="success-content">
            <Text className="success-message">
              Il tuo account è stato creato con successo ed è ora in attesa di
              approvazione da parte dell'amministratore.
            </Text>
            <div className="pending-notice">
              <ClockCircleOutlined className="pending-icon" />
              <Text strong className="pending-text">
                Account in Revisione
              </Text>
            </div>
            <Text className="instruction-text">
              Riceverai una notifica via email non appena il tuo account sarà
              attivato. Nel frattempo, puoi riprovare ad accedere più tardi.
            </Text>
          </div>
        }
        extra={[
          <Button
            key="login"
            type="primary"
            size="large"
            icon={<LoginOutlined />}
            onClick={handleGoToLogin}
            className="success-button primary"
          >
            Vai al Login
          </Button>,
          <Button
            key="back"
            size="large"
            onClick={handleBackToRegistration}
            className="success-button secondary"
          >
            Torna Indietro
          </Button>,
        ]}
      />
    </div>
  );

  const tabItems = [
    {
      key: "login",
      label: (
        <Space>
          <LoginOutlined />
          <span>Accesso</span>
        </Space>
      ),
      children: (
        <div className="auth-form-container">
          {alert && activeTab === "login" && (
            <Alert
              message={alert.message}
              type={alert.type}
              showIcon
              className="auth-alert"
            />
          )}

          <div className="form-header">
            <Title level={3} className="form-title">
              Bentornato!
            </Title>
            <Text className="form-subtitle">
              Accedi al tuo account per continuare
            </Text>
          </div>

          <Form
            form={loginForm}
            name="login"
            onFinish={handleLogin}
            onFinishFailed={onLoginFinishFailed}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: "Inserisci il tuo email!" }]}
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder="example@domain.com"
                className="auth-input"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Inserisci la tua password!" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                placeholder="••••••••"
                className="auth-input"
              />
            </Form.Item>

            <Form.Item className="submit-button-container">
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className="auth-submit-button"
                icon={<LoginOutlined />}
              >
                Accedi
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    {
      key: "register",
      label: (
        <Space>
          <UserAddOutlined />
          <span>Crea Account</span>
        </Space>
      ),
      children: registrationSuccess ? (
        <RegistrationSuccessView />
      ) : (
        <div className="auth-form-container">
          {alert && activeTab === "register" && (
            <Alert
              message={alert.message}
              type={alert.type}
              showIcon
              className="auth-alert"
            />
          )}

          <div className="form-header">
            <Title level={3} className="form-title">
              Crea il tuo account
            </Title>
            <Text className="form-subtitle">
              Unisciti alla piattaforma Traffix
            </Text>
          </div>

          <Form
            form={form}
            name="register"
            onFinish={handleRegister}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="firstName"
                  label="Nome"
                  rules={[
                    { required: true, message: "Inserisci il tuo nome!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="input-icon" />}
                    placeholder="Mario"
                    className="auth-input"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="lastName"
                  label="Cognome"
                  rules={[
                    { required: true, message: "Inserisci il tuo cognome!" },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="input-icon" />}
                    placeholder="Rossi"
                    className="auth-input"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="client" label="Cliente">
              <Input
                prefix={<UserOutlined className="input-icon" />}
                placeholder="azienda"
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Inserisci la tua email!" },
                {
                  type: "email",
                  message: "Inserisci un indirizzo email valido!",
                },
              ]}
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder="example@domain.com"
                className="auth-input"
              />
            </Form.Item>

            <Form.Item
              name="address"
              label="Indirizzo"
              rules={[
                { required: true, message: "Inserisci il tuo indirizzo!" },
              ]}
            >
              <Input
                prefix={<HomeOutlined className="input-icon" />}
                placeholder="Via Roma 123, Milano"
                className="auth-input"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="gender"
                  label="Genere"
                  rules={[
                    { required: true, message: "Seleziona il tuo genere!" },
                  ]}
                >
                  <Select
                    placeholder="Seleziona genere"
                    options={genderOptions}
                    className="auth-select"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label="Numero di Telefono"
                  rules={[
                    {
                      required: true,
                      message: "Inserisci il tuo numero di telefono!",
                    },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined className="input-icon" />}
                    placeholder="+39 123 456 7890"
                    className="auth-input"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Inserisci la tua password!" },
                { min: 6, message: "Minimo 6 caratteri!" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                placeholder="••••••••"
                className="auth-input"
              />
            </Form.Item>

            <Form.Item className="submit-button-container">
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className="auth-submit-button"
                icon={<UserAddOutlined />}
              >
                Crea Account
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#A1E340",
          borderRadius: 8,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      }}
    >
      <div className="auth-page-container">
        <div className="auth-background">
          <div className="auth-background-overlay"></div>
        </div>

        <div className="auth-content">
          <div className="auth-brand">
            <div className="logo-hero-wrap">
              <img src={traffixLogo} alt="Traffix" className="logo-hero" />
            </div>
            <Text className="brand-subtitle">
              Sistema di Gestione del Traffico
            </Text>
          </div>

          <Card className="auth-card">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                form.resetFields();
                setAlert(null);
                setRegistrationSuccess(false);
              }}
              centered
              items={tabItems}
              className="auth-tabs"
            />
          </Card>
        </div>

        <footer className="auth-footer">
          <div className="footer-exis-wrap">
            <img src={logo} alt="Exis Mobility" className="logo-exis-footer" />
          </div>
          <div className="white-bar"></div>
          <p>
            © {new Date().getFullYear()} Exis Mobility. Tutti i diritti
            riservati.
          </p>
        </footer>
      </div>

    </ConfigProvider>
  );
};

export default MysteryClient;
